import { useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo } from "react";
import useCatalogForm from "../../../../hooks/use-catalog-form/useCatalogForm";
import useCatalogApi from "../../../../hooks/useCatalogApi";
import useModalLayout from "../../../../hooks/use-modal-layout/useModalLayout";
import agent from "../../../../agent";

const baseInitialValues = {
    name: "",
    shortName: "",
    cipher: "",
    basicValue: null,
    tax: null,
    amountIndicat: "",
    startedAt: null,
    endedAt: null,
    taxStartedAt: null,
    taxEndedAt: null,
    approvedAt: null,
    activity: true,
    taxActivity: true,
    maxIncome: "",
    maxSum: "",
    minIncome: "",
    minSum: "",
    operationTypeTaxes: [],
    newOperationTypeTaxes: [],
};

const defineFieldProp = (prop, code) => {
    switch (prop) {
        case "name":
            return prop.concat("Ru");
        case "shortName":
            return code === "basic-values" ? "sign" : prop.concat("Ru");
        case "cipher":
            return prop.concat("Code");
        default:
            return prop;
    }
}

const getByPath = (obj, path) => path.split(".").reduce((acc, key) => acc?.[key], obj);

const buildInitialValues = (type, code, currentRecord, method) => {
    if (method !== "PUT" || !currentRecord) {
        return baseInitialValues;
    }

    const mapped = Object.entries(baseInitialValues).reduce(
        (acc, [key, defaultVal]) => {
            const apiKey = defineFieldProp(key, code);
            const propValue = getByPath(currentRecord, apiKey);

            return {
                ...acc,
                [key]: propValue
                    ? propValue
                    : defaultVal,
            };
        },
        { ...baseInitialValues }
    );

    switch (type) {
        case "monitoring":
            return {
                ...baseInitialValues,
                name: mapped.name,
            };
        case "billing":
            switch (code) {
                case "basic-values":
                    return {
                        ...baseInitialValues,
                        name: mapped.name,
                        shortName: mapped.shortName,
                    };
                case "indicators":
                    return {
                        ...baseInitialValues,
                        name: mapped.name,
                        shortName: mapped.shortName,
                        cipher: mapped.cipher,
                        basicValue: mapped.basicValue || null,
                        amountIndicat: mapped.amountIndicat,
                        startedAt: mapped.startedAt ? new Date(mapped.startedAt) : null,
                        endedAt: mapped.endedAt ? new Date(mapped.endedAt) : null,
                        activity: mapped.activity,
                    };
                case "taxes":
                    return {
                        ...baseInitialValues,
                        name: mapped.name,
                        shortName: mapped.shortName,
                        basicValue: mapped.basicValue || null,
                        amountIndicat: mapped.amountIndicat,
                        startedAt: mapped.startedAt ? new Date(mapped.startedAt) : null,
                        endedAt: mapped.endedAt ? new Date(mapped.endedAt) : null,
                        maxIncome: mapped.maxIncome,
                        minIncome: mapped.minIncome,
                        activity: mapped.activity,
                    };
                case "operation-types":
                    const existingTaxes = mapped.operationTypeTaxes.map((item) => ({
                        taxId: item.taxId,
                        maxSum: item.maxSum,
                        minSum: item.minSum,
                    }));

                    return {
                        ...baseInitialValues,
                        name: mapped.name,
                        cipher: mapped.cipher,
                        operationTypeTaxes: existingTaxes,
                        newOperationTypeTaxes: [],
                        startedAt: mapped.startedAt ? new Date(mapped.startedAt) : null,
                        endedAt: mapped.endedAt ? new Date(mapped.endedAt) : null,
                        approvedAt: mapped.approvedAt ? new Date(mapped.approvedAt) : null,
                        activity: mapped.activity,
                    }
                default:
                    console.error("Unknown billing code:", code);
                    return mapped;
            }
        default:
            console.error("Unknown type:", type);
            return mapped;
    }
};

const useRecordActionModal = ({
    method, currentTab, isVisible, setIsVisible, fetchData, currentRecord = null, type = "monitoring"
}) => {
    let modalLabel;

    switch (method) {
        case "GET":
            modalLabel = `Добавить ${currentTab.modalLabel}`;
            break;
        case "PUT":
            modalLabel = `Редактировать ${currentTab.modalLabel}`;
            break;
        default:
            modalLabel = "";
    }

    const headerConfig = { title: modalLabel, className: "modalTitle" };

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        shouldFocusError: true,
        defaultValues: baseInitialValues,
    });

    const catalogForm = useCatalogForm(
        { control, errors, watch, setValue }, type, currentTab.code
    );
    const { handleCatalogRequest } = useCatalogApi();

    useEffect(() => {
        if (!isVisible) {
            reset(buildInitialValues(type, currentTab.code, null));
            return;
        }

        if (method === "PUT" && currentRecord) {
            reset(buildInitialValues(type, currentTab.code, currentRecord, method));
        } else {
            reset(buildInitialValues(type, currentTab.code, null, method));
        }
    }, [isVisible, method, type, currentTab.code, currentRecord, reset]);

    const formatNullishNumber = (value) => {
        if (value == null || value === "") {
            return null;
        }

        const number = Number(String(value).replace(",", "."));

        return Number.isNaN(number) ? null : number;
    };

    const formatDate = (value) => {
        if (!value) {
            return null;
        }

        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) {
            return null;
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const onSubmit = useCallback(
        async ({
                   name,
                   shortName,
                   cipher,
                   basicValue,
                   amountIndicat,
                   startedAt,
                   endedAt,
                   approvedAt,
                   activity,
                   maxIncome,
                   minIncome,
                   operationTypeTaxes,
                   newOperationTypeTaxes
               }) => {
            switch (type) {
                case "monitoring":
                    await handleCatalogRequest(method, [
                        {
                            callback: agent.CatalogType.addCatalogType,
                            params: [currentTab.code, { name: name }],
                        },
                        {
                            callback: agent.CatalogType.updateCatalogType,
                            params: [
                                currentTab.code,
                                currentRecord?.id,
                                { name: name },
                            ],
                        },
                    ]);
                    break;

                case "billing": {
                    let upsertDto;

                    switch (currentTab.code) {
                        case "basic-values":
                            upsertDto = {
                                nameRu: name,
                                sign: shortName,
                            };
                            break;
                        case "indicators":
                            upsertDto = {
                                nameRu: name,
                                shortNameRu: shortName,
                                cipherCode: cipher,
                                basicValue: basicValue,
                                amountIndicat: formatNullishNumber(amountIndicat),
                                startedAt: formatDate(startedAt),
                                endedAt: formatDate(endedAt),
                                activity: Boolean(activity),
                            };
                            break;
                        case "taxes":
                            upsertDto = {
                                nameRu: name,
                                shortNameRu: shortName,
                                basicValue: basicValue,
                                amountIndicat: formatNullishNumber(amountIndicat),
                                startedAt: formatDate(startedAt),
                                endedAt: formatDate(endedAt),
                                maxIncome: formatNullishNumber(maxIncome),
                                minIncome: formatNullishNumber(minIncome),
                                activity: Boolean(activity),
                            };
                            break;
                        case "operation-types":
                            const mergedTaxes = [
                                ...operationTypeTaxes,
                                ...newOperationTypeTaxes.filter(
                                    (newTax) => !operationTypeTaxes.some((baseTax) => baseTax.taxId === newTax.taxId)
                                ),
                            ];

                            upsertDto = {
                                nameRu: name,
                                cipherCode: cipher,
                                operationTypeTaxes: mergedTaxes,
                                startedAt: formatDate(startedAt),
                                endedAt: formatDate(endedAt),
                                approvedAt: formatDate(approvedAt),
                                activity: Boolean(activity),
                            };
                            break;
                        default:
                            console.error("Incorrect code: Use basic-values, indicators, taxes or operation-types");
                            break;
                    }

                    if (!upsertDto) {
                        return;
                    }

                    await handleCatalogRequest(method, [
                        {
                            callback: agent.AccountingDictionary.addRecord,
                            params: [currentTab.code, upsertDto],
                        },
                        {
                            callback: agent.AccountingDictionary.updateRecord,
                            params: [currentTab.code, currentRecord?.id, upsertDto],
                        },
                    ]);
                    break;
                }

                default:
                    console.error("Incorrect type: Use monitoring or billing");
                    break;
            }

            await fetchData();
            setIsVisible(false);
        }, [
            type,
            method,
            currentTab.code,
            currentRecord?.id,
            fetchData,
            setIsVisible,
            formatNullishNumber,
            formatDate,
            handleCatalogRequest,
        ]
    );

    const handleClose = useCallback(() => {
        setIsVisible(false);
        reset(buildInitialValues(type, currentTab.code, null, method));
    }, [setIsVisible, reset, type, currentTab.code, method]);

    const isSame = useMemo(() => {
        if (!currentRecord || method !== "PUT") {
            return false;
        }

        const initialForRecord = buildInitialValues(type, currentTab.code, currentRecord, method);

        return Object.entries(initialForRecord).every(([key, value]) => watch(key) === value);
    }, [currentRecord, method, type, currentTab.code, watch]);

    const footerConfig = {
        buttons: [
            {
                label: "Отмена",
                onClick: handleClose,
                classNames: ["actionButton", "cancelButton"],
            },
            {
                label: "Сохранить",
                onClick: handleSubmit(onSubmit),
                classNames: ["actionButton"],
                disabled: isSubmitting || isSame,
            },
        ]
    };

    const { header, footer } = useModalLayout(headerConfig, footerConfig);

    return {
        catalogForm,
        submitHandler: () => handleSubmit(onSubmit),
        handleClose,
        header,
        footer
    }
};

export default useRecordActionModal;
